import { useCallback, useState } from 'react'
import { CIMSModal } from '../../../CIMSModal'
import {
  Create,
  Custom,
  CustomCreateMutationModalOptions,
  CustomEditMutationModalOptions,
  Edit,
  MutationModalProps,
  Simple,
  SimpleCreateModalOptions,
  SimpleEditModalOptions,
} from './types'

/**
 * a mutation modal component for the `CIMSTable` component
 *
 * returns null, the provided complex modal, or a simple modal
 */
export function MutationModal<T, U>({
  onClose,
  modal,
  ...rest
}: MutationModalProps & {
  modal:
    | ({
        props:
          | (CustomCreateMutationModalOptions & Create)
          | (CustomEditMutationModalOptions<T> & Edit<T>)
      } & Custom)
    | ({
        props:
          | (SimpleCreateModalOptions & Create)
          | (Omit<SimpleEditModalOptions<T, U>, 'recordName'> & Edit<T>)
      } & Required<Simple>)
}) {
  const [mutating, setMutating] = useState(false)
  const close = useCallback(async () => {
    if (modal.type === 'simple') {
      if (modal.props.type === 'edit')
        await modal.props.onClose?.(modal.props.record)
      else await modal.props.onClose?.()
    }
    onClose()
  }, [modal, onClose])
  const ok = useCallback(
    (results: any) => {
      if (modal.type === 'custom') return
      if (modal.props.type === 'create') return modal.props.onOk(results)
      else return modal.props.onOk(modal.props.record, results)
    },
    [modal]
  )

  let body
  if (modal.type === 'custom') {
    if (modal.props.type === 'create') return null
  } else if (modal.props.type === 'create') body = modal.props.body
  else
    body =
      typeof modal.props.body === 'function'
        ? modal.props.body(modal.props.record)
        : modal.props.body

  const initialFormValues =
    modal.type === 'simple' && modal.props.type === 'edit'
      ? modal.props.initialValues?.(modal.props.record)
      : undefined

  let height, width
  if (modal.type === 'simple') {
    height = modal.props.height
    width = modal.props.width
  }

  return (
    <CIMSModal
      {...rest}
      height={height}
      width={width}
      initialFormValues={initialFormValues}
      onCancel={async () => {
        await close()
      }}
      onOk={async (results) => {
        try {
          setMutating(true)
          await ok(results)
          await close()
        } finally {
          setMutating(false)
        }
      }}
      loading={mutating}
      disabled={mutating}
    >
      {body}
    </CIMSModal>
  )
}
